import { css } from 'aphrodite';
import { observer } from 'mobx-react';
import { useEffect, useMemo } from 'react';
import { Waypoint } from 'react-waypoint';
import * as Api from '../../../../../../extViewmodels';
import { LoadingSpinner } from '../../../../../components/LoadingSpinner';
import { useContactTimeline } from '../../../../../components/entities/EntityTimeline/hooks/useTimeline';
import { ContactTimelineList } from './list';
import { styleSheet } from './styles';

interface IProps {
	contactId: string;
	contactName: string;
	emailAddresses: Api.EmailAddress[];
	eventTypes: Api.TimelineEventTypes[];
	expandAllTimeline?: boolean;
	onDeleteRichContent: (richContent: Api.RichContentViewModel) => Promise<Api.IRichContent>;
	onMoreMenuOptionClicked?: (
		richContent: Api.RichContentViewModel,
		option: 'edit' | 'delete',
		e: React.MouseEvent<HTMLElement>
	) => void;
	setShouldStartPollingForRelationshipUpdate: (poll: boolean) => void;
	onTimelineReady?: (invalidateTimeline: () => void) => void;
}

export const ContactTimelineV2 = observer(
	({
		contactId,
		contactName,
		emailAddresses,
		eventTypes,
		expandAllTimeline,
		onDeleteRichContent,
		onMoreMenuOptionClicked,
		setShouldStartPollingForRelationshipUpdate,
		onTimelineReady,
	}: IProps) => {
		const timelineProps = useContactTimeline({
			contactId,
			emailAddresses,
			eventTypes,
			setShouldStartPollingForRelationshipUpdate,
		});

		// Call the onTimelineReady callback with the invalidate function
		useEffect(() => {
			if (onTimelineReady) {
				onTimelineReady(() => timelineProps.timelineData.invalidateTimeline());
			}
		}, [onTimelineReady, timelineProps.timelineData]);

		const timelinePages = useMemo(() => {
			if (!timelineProps.timelineData.data) {
				return [];
			}
			return timelineProps.timelineData.data.pages;
		}, [timelineProps.timelineData.data]);

		const timelineGroups = useMemo(() => {
			return timelinePages.map(group => {
				return group.values.filter(event => {
					// Sometimes the messages in conversation thread events are empty.
					// If an events last messages are all empty, skip the event in the timeline.
					if (!(event instanceof Date)) {
						const data = event.toJs();
						if (data._type === 'ConversationThreadEvent') {
							const conversationThreadEvent = data as Api.IConversationThreadEvent;
							if (!conversationThreadEvent.lastMessages?.some(msg => msg.text || msg.media)) {
								return false;
							}
						}
					}
					return true;
				});
			});
		}, [timelinePages]);

		return (
			<div className={css(styleSheet.timelineContainer)}>
				<ContactTimelineList
					items={timelineGroups}
					contactEmailAddresses={emailAddresses}
					contactName={contactName}
					expandAllTimeline={expandAllTimeline}
					onDeleteRichContent={onDeleteRichContent}
					onMoreMenuOptionClicked={onMoreMenuOptionClicked}
					onTimelineDataUpdated={() => timelineProps.timelineData.invalidateTimeline()}
				/>

				{(timelineProps.timelineData.isLoading || timelineProps.timelineData.isFetchingNextPage) && <LoadingSpinner />}

				{timelineProps.timelineData.hasNextPage && !timelineProps.timelineData.isFetchingNextPage ? (
					<Waypoint
						bottomOffset='-200px'
						onEnter={() => {
							timelineProps.timelineData.fetchNextPage();
						}}
					/>
				) : null}
			</div>
		);
	}
);
